import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RepeatIcon from "@mui/icons-material/Repeat";
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../components/UIToolkit/molecules/InlineHint";
import { JobSelector } from "../../../components/UIToolkit/molecules/JobSelector";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid/SystemDataGrid";
import { useJobs } from "../../../hooks/useJobs";
import type { PbjPrediction, PbjPredictionHoursMode, PbjPredictionManualHoursEntry } from "../../../utils/models";
import {
	useCreatePbjPredictionManualHours,
	useDeletePbjPredictionManualHours,
	usePbjPredictionJobCoverage,
	usePbjPredictionManualHours,
	useUpdatePbjPredictionManualHours,
} from "../hooks/usePbjPredictionManualHours";
import { brandBorder, brandTint, brandWash } from "./colors";
import { WEEKEND_CATEGORY_ID } from "./cumulativeScoring.utils";
import { MissingJobsWarning } from "./MissingJobsWarning";
import {
	effectiveWindowEnd,
	formatHoursValue,
	parseHours,
	perDayRateForEntry,
	periodDayCounts,
	reportedHrdDeltaForEntry,
	reportedWeekendHrdDeltaForEntry,
	resolveTotalHours,
	scaleFor,
} from "./manualHoursHppd.utils";
import { PbjPredictionJobCoverageStatus } from "./PbjPredictionJobCoverageStatus";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
	// Committed audit window — the FE list query keys off this, and the BE prorates
	// any out-of-window entry. We default the inline-add form's period to this same
	// window so a same-quarter add is one click away.
	startDate: string; // ISO yyyy-mm-dd
	endDate: string; // ISO yyyy-mm-dd
	// Snapshot of the prediction so the header can render the projected-HPPD-delta
	// preview (without a recompute roundtrip). `resolvedAverageCensus` * `days` is
	// the denominator a single in-window hour moves the dial by; weekend HPPD uses
	// `weekendDays` as the denominator instead.
	prediction: PbjPrediction;
	// Seed for the add-form's "Period start". Defaults to the current quarter's first day, which is
	// right on the Prediction tab: that tab reasons about the quarter as a whole, so an entry
	// spanning it is what a user means. It is wrong on the TRACKER, where an entry dated before the
	// plan was saved lands in the baseline and raises the bar instead of clearing it — the quarter
	// default there seeds precisely the span that cannot register as progress.
	defaultPeriodStart?: DateTime;
};

// Hours bounds — match the BE `_PREDICTION_HOURS_MIN/MAX`. Per-bucket can be 0
// (admin can zero out one side), but at least ONE bucket must be > 0 — enforced
// in `canSubmit` below and the BE validator.
const HOURS_MIN = 0;
const HOURS_MAX = 100_000;
const HOURS_STEP = 0.5;
// A per-day rate is bounded by the day itself. The stored TOTAL still gets the wide bound above,
// because a recurring entry legitimately multiplies up past it.
const PER_DAY_HOURS_MAX = 24;

type FormState = {
	editingId: string | null;
	jobId: string | null;
	isRn: boolean;
	employeeName: string;
	hoursMode: PbjPredictionHoursMode;
	weekdayHours: string;
	weekendHours: string;
	periodStart: DateTime | null;
	periodEnd: DateTime | null;
};

const EMPLOYEE_NAME_MAX_LENGTH = 255;

// Default the inline-add form to the CURRENT calendar quarter regardless of
// which audit window the user happens to be viewing — most ad-hoc adjustments
// target the active CMS quarter, so seed that and let the user override.
const currentQuarterRange = (): { start: DateTime; end: DateTime } => {
	const now = DateTime.now();
	const start = now.startOf("quarter");
	const end = now.endOf("quarter").startOf("day");
	return { start, end };
};

const blankForm = (periodStart?: DateTime): FormState => {
	const { start, end } = currentQuarterRange();
	return {
		editingId: null,
		jobId: null,
		isRn: false,
		employeeName: "",
		// DEFAULT: `recurring` is the entry mode this dialog is built around — an admin nurse works a
		// shift pattern, not a lump sum, and typing "8" is the whole point of DAT-608. `total` stays
		// one click away for the genuine lump sums (a block of orientation hours) it was built for.
		hoursMode: "recurring",
		weekdayHours: "",
		weekendHours: "",
		periodStart: periodStart ?? start,
		periodEnd: end,
	};
};

// Category-id constants matching the BE category ids in `scoring.py` —
// 1: Adjusted Total Nurse HPPD, 2: Adjusted RN HPPD. The Weekend id ("3") comes from
// the shared WEEKEND_CATEGORY_ID constant.
const CATEGORY_ID_TOTAL_HPPD = "1";
const CATEGORY_ID_RN_HPPD = "2";

// A non-empty hours field must be a finite number within [HOURS_MIN, max]; empty is allowed
// (treated as 0 above). The ceiling tightens to 24 in recurring mode — see PER_DAY_HOURS_MAX.
const isHoursFieldValid = (raw: string, max: number): boolean => {
	const trimmed = raw.trim();
	if (trimmed === "") return true;
	const n = Number(trimmed);
	return Number.isFinite(n) && n >= HOURS_MIN && n <= max;
};

export const ManageManualHoursDialog: FC<Props> = ({ open, onClose, departmentId, startDate, endDate, prediction, defaultPeriodStart }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const jobs = useJobs();
	const listQuery = usePbjPredictionManualHours(departmentId, startDate, endDate);
	// Same gate as the Prediction tab's banner — the missing-jobs list is about SCHEDULED hours, so it
	// says nothing useful about a score built from payroll punches (DAT-597).
	const auditHoursSource = prediction.hoursSource === "audit";
	const coverageQuery = usePbjPredictionJobCoverage(departmentId, startDate, endDate, !auditHoursSource);
	const createMutation = useCreatePbjPredictionManualHours();
	const updateMutation = useUpdatePbjPredictionManualHours();
	const deleteMutation = useDeletePbjPredictionManualHours();
	const [form, setForm] = useState<FormState>(() => blankForm(defaultPeriodStart));

	const entries = listQuery.data ?? [];

	// Each adjusted HPPD bucket has its own case-mix `scale` so we have to convert
	// reported HRD → adjusted HPPD per-bucket. Keeping these out of the loop avoids
	// re-finding the category for every entry.
	const totalScale = scaleFor(prediction, CATEGORY_ID_TOTAL_HPPD);
	const rnScale = scaleFor(prediction, CATEGORY_ID_RN_HPPD);
	const weekendScale = scaleFor(prediction, WEEKEND_CATEGORY_ID);

	// Effective prediction window the BE used to pro-rate manual hours: starts at
	// the audit window's start, ends at `dataThroughDate = min(end_date, today)`
	// (or `endDate` when the quarter is fully in the future and BE nulls it).
	const windowStart = DateTime.fromISO(startDate).startOf("day");
	const windowEnd = effectiveWindowEnd(prediction, endDate);

	// Aggregate Δ-hppd previews so the header can show current vs. projected stars-
	// adjacent numbers without a recompute. Total sums every entry; RN sums only
	// `isRn=true` entries (RN ⊂ Total); Weekend sums only the weekend share —
	// regardless of `isRn`, since Weekend HPPD is not RN-gated in scoring.
	// We sum REPORTED HRDs first, then apply each bucket's adjusted-per-reported
	// scale once — same math the BE uses, expressed in the same adjusted-HPPD
	// units the "current" display shows so the projected delta visually adds up.
	const { totalDelta, rnDelta, weekendDelta } = useMemo(() => {
		let totalReported = 0;
		let rnReported = 0;
		let weekendReported = 0;
		for (const e of entries) {
			const td = reportedHrdDeltaForEntry(e, prediction, windowStart, windowEnd);
			totalReported += td;
			if (e.isRn) rnReported += td;
			weekendReported += reportedWeekendHrdDeltaForEntry(e, prediction, windowStart, windowEnd);
		}
		return {
			totalDelta: totalReported * totalScale,
			rnDelta: rnReported * rnScale,
			weekendDelta: weekendReported * weekendScale,
		};
	}, [entries, prediction, totalScale, rnScale, weekendScale, windowStart, windowEnd]);

	const isRecurring = form.hoursMode === "recurring";
	const hoursFieldMax = isRecurring ? PER_DAY_HOURS_MAX : HOURS_MAX;
	// Day counts for the period currently in the form — the multiplier a recurring entry resolves
	// through, and the denominator the "8 h × 65 weekdays" hint reads from.
	const formDayCounts = useMemo(
		() => periodDayCounts(form.periodStart?.toISODate() ?? "", form.periodEnd?.toISODate() ?? ""),
		[form.periodStart, form.periodEnd],
	);

	const weekdayValue = parseHours(form.weekdayHours);
	const weekendValue = parseHours(form.weekendHours);
	const weekdayValid = weekdayValue !== null && weekdayValue >= HOURS_MIN && weekdayValue <= hoursFieldMax;
	const weekendValid = weekendValue !== null && weekendValue >= HOURS_MIN && weekendValue <= hoursFieldMax;
	// What actually gets stored — the resolved period totals. In `total` mode these ARE the typed
	// values; in `recurring` mode they are the typed rate times the period's day count.
	const weekdayTotal = resolveTotalHours(weekdayValue ?? 0, form.hoursMode, formDayCounts.weekday);
	const weekendTotal = resolveTotalHours(weekendValue ?? 0, form.hoursMode, formDayCounts.weekend);
	// Mirror BE rule: at least one bucket must be > 0 (use delete to clear an entry, not a
	// both-zero update). Checked on the RESOLVED totals, since 8 h/day over a period with no
	// weekend days stores zero weekend hours no matter what was typed.
	const hasPositive = weekdayTotal > 0 || weekendTotal > 0;
	const dateRangeInvalid = form.periodStart === null || form.periodEnd === null || form.periodEnd.startOf("day") < form.periodStart.startOf("day");
	const canSubmit =
		form.jobId !== null && weekdayValid && weekendValid && hasPositive && !dateRangeInvalid && !createMutation.isPending && !updateMutation.isPending;

	const handleEdit = useCallback((entry: PbjPredictionManualHoursEntry) => {
		// Re-seed the field the entry was TYPED in: a recurring entry shows its per-day rate back,
		// not the multiplied-out total it happens to be stored as.
		const perDay = perDayRateForEntry(entry);
		const recurring = entry.hoursMode === "recurring";
		setForm({
			editingId: entry.id,
			jobId: entry.jobId,
			isRn: entry.isRn,
			employeeName: entry.employeeName ?? "",
			hoursMode: entry.hoursMode,
			weekdayHours: recurring ? formatHoursValue(perDay.weekday) : String(entry.weekdayHours),
			weekendHours: recurring ? formatHoursValue(perDay.weekend) : String(entry.weekendHours),
			periodStart: DateTime.fromISO(entry.periodStartDate),
			periodEnd: DateTime.fromISO(entry.periodEndDate),
		});
	}, []);

	const handleResetForm = useCallback(() => {
		setForm(blankForm(defaultPeriodStart));
	}, [defaultPeriodStart]);

	const handleClose = useCallback(() => {
		if (createMutation.isPending || updateMutation.isPending) return;
		handleResetForm();
		onClose();
	}, [createMutation.isPending, updateMutation.isPending, handleResetForm, onClose]);

	// Switching mode re-interprets whatever is typed (8 means "8 total" vs "8 a day"), so the
	// fields are cleared rather than silently changing meaning under the user's cursor.
	const handleModeChange = useCallback((mode: PbjPredictionHoursMode) => {
		setForm((prev) => ({ ...prev, hoursMode: mode, weekdayHours: "", weekendHours: "" }));
	}, []);

	const handleSubmit = useCallback(() => {
		if (!canSubmit || form.periodStart === null || form.periodEnd === null || form.jobId === null) return;
		const trimmedName = form.employeeName.trim();
		const body = {
			departmentId,
			periodStartDate: form.periodStart.toISODate() ?? startDate,
			periodEndDate: form.periodEnd.toISODate() ?? endDate,
			isRn: form.isRn,
			weekdayHours: weekdayTotal,
			weekendHours: weekendTotal,
			employeeName: trimmedName === "" ? null : trimmedName,
			jobId: form.jobId,
			hoursMode: form.hoursMode,
		};
		if (form.editingId !== null) {
			updateMutation.mutate({ entryId: form.editingId, body }, { onSuccess: () => handleResetForm() });
		} else {
			createMutation.mutate(body, { onSuccess: () => handleResetForm() });
		}
	}, [canSubmit, weekdayTotal, weekendTotal, form, departmentId, startDate, endDate, createMutation, updateMutation, handleResetForm]);

	const handleDelete = useCallback(
		(entryId: string) => {
			deleteMutation.mutate({ entryId, departmentId });
		},
		[deleteMutation, departmentId],
	);

	const columns = useMemo<GridColDef<PbjPredictionManualHoursEntry>[]>(
		() => [
			{
				field: "isRn",
				headerName: t("pbj_model.prediction.manual_hours.col_type"),
				width: 100,
				renderCell: (params) => (
					<Chip
						size="small"
						label={params.row.isRn ? t("pbj_model.prediction.manual_hours.type_rn") : t("pbj_model.prediction.manual_hours.type_non_rn")}
						sx={{
							bgcolor: params.row.isRn ? brandTint(theme) : undefined,
							color: params.row.isRn ? accent : undefined,
							fontWeight: 600,
						}}
					/>
				),
			},
			{
				field: "jobDisplayName",
				headerName: t("pbj_model.prediction.manual_hours.col_job"),
				flex: 1,
				minWidth: 150,
				// Rows written before jobs were captured genuinely have no job — an em dash says so
				// rather than implying one was chosen.
				valueGetter: (_value, row) => row.jobDisplayName ?? "—",
				renderCell: (params) => (
					<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", minWidth: 0 }}>
						<Typography variant="body2" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>
							{params.row.jobDisplayName ?? "—"}
						</Typography>
						{params.row.hoursMode === "recurring" && (
							<Tooltip title={t("pbj_model.prediction.manual_hours.recurring_badge_tooltip")}>
								<RepeatIcon sx={{ fontSize: 15, color: "text.secondary" }} data-testid={`pbj-prediction-manual-hours-recurring-${params.row.id}`} />
							</Tooltip>
						)}
					</Stack>
				),
			},
			{
				field: "employeeName",
				headerName: t("pbj_model.prediction.manual_hours.col_employee"),
				flex: 1,
				minWidth: 130,
				valueGetter: (_value, row) => row.employeeName ?? "—",
			},
			{
				field: "periodStartDate",
				headerName: t("pbj_model.prediction.manual_hours.col_period"),
				flex: 1,
				minWidth: 180,
				valueGetter: (_value, row) =>
					`${DateTime.fromISO(row.periodStartDate).toFormat("MM/dd/yyyy")} → ${DateTime.fromISO(row.periodEndDate).toFormat("MM/dd/yyyy")}`,
			},
			{
				field: "weekdayHours",
				headerName: t("pbj_model.prediction.manual_hours.col_weekday_hours"),
				width: 130,
				type: "number",
				valueGetter: (_value, row) => row.weekdayHours,
			},
			{
				field: "weekendHours",
				headerName: t("pbj_model.prediction.manual_hours.col_weekend_hours"),
				width: 130,
				type: "number",
				valueGetter: (_value, row) => row.weekendHours,
			},
			{
				field: "totalDelta",
				headerName: t("pbj_model.prediction.manual_hours.col_total_delta"),
				width: 130,
				type: "number",
				valueGetter: (_value, row) => Number((reportedHrdDeltaForEntry(row, prediction, windowStart, windowEnd) * totalScale).toFixed(4)),
			},
			{
				field: "rnDelta",
				headerName: t("pbj_model.prediction.manual_hours.col_rn_delta"),
				width: 130,
				type: "number",
				valueGetter: (_value, row) => (row.isRn ? Number((reportedHrdDeltaForEntry(row, prediction, windowStart, windowEnd) * rnScale).toFixed(4)) : 0),
			},
			{
				field: "weekendDelta",
				headerName: t("pbj_model.prediction.manual_hours.col_weekend_delta"),
				width: 150,
				type: "number",
				valueGetter: (_value, row) => Number((reportedWeekendHrdDeltaForEntry(row, prediction, windowStart, windowEnd) * weekendScale).toFixed(4)),
			},
			{
				field: "actions",
				headerName: "",
				width: 110,
				sortable: false,
				filterable: false,
				renderCell: (params) => (
					<Box sx={{ display: "flex", gap: 0.5 }}>
						<Tooltip title={t("pbj_model.prediction.manual_hours.edit")}>
							<IconButton size="small" onClick={() => handleEdit(params.row)} data-testid={`pbj-prediction-manual-hours-edit-${params.row.id}`}>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title={t("pbj_model.prediction.manual_hours.delete")}>
							<IconButton
								size="small"
								onClick={() => handleDelete(params.row.id)}
								disabled={deleteMutation.isPending}
								data-testid={`pbj-prediction-manual-hours-delete-${params.row.id}`}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				),
			},
		],
		[t, theme, accent, prediction, totalScale, rnScale, weekendScale, windowStart, windowEnd, handleEdit, handleDelete, deleteMutation.isPending],
	);

	const currentTotalHppd = prediction.categories.find((c) => c.id === "1");
	const currentRnHppd = prediction.categories.find((c) => c.id === "2");
	const currentWeekendHppd = prediction.categories.find((c) => c.id === WEEKEND_CATEGORY_ID);

	// Per-field hint under each hours input: in recurring mode it shows the multiplication being
	// performed (the ticket's "populate the result automatically"), and before anything is typed it
	// still states the day count so the user knows what they'd be multiplying by.
	const hoursHint = (typed: number | null, total: number, dayCount: number, dayCountLabel: string): string => {
		if (!isRecurring) return t("pbj_model.prediction.manual_hours.total_hours_hint");
		if (typed === null || typed <= 0) return dayCountLabel;
		return t("pbj_model.prediction.manual_hours.recurring_hint", {
			rate: formatHoursValue(typed),
			days: dayCount,
			total: formatHoursValue(total),
		});
	};

	return (
		<SystemDialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			dataTestId="pbj-prediction-manage-manual-hours-dialog"
			title={t("pbj_model.prediction.manual_hours.dialog_title")}
			hideCancel
			confirm={t("pbj_model.prediction.manual_hours.done")}
			confirmButtonVariant="secondary"
			onConfirm={handleClose}
			body={
				<Stack spacing={2}>
					<Box
						sx={{
							p: 1.5,
							bgcolor: brandWash(theme),
							border: "1px solid",
							borderColor: brandBorder(theme),
							borderRadius: 1.5,
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{t("pbj_model.prediction.manual_hours.preview_header")}
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
							{t("pbj_model.prediction.manual_hours.preview_total", {
								current: currentTotalHppd ? currentTotalHppd.value : "—",
								delta: totalDelta.toFixed(3),
							})}
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
							{t("pbj_model.prediction.manual_hours.preview_rn", {
								current: currentRnHppd ? currentRnHppd.value : "—",
								delta: rnDelta.toFixed(3),
							})}
						</Typography>
						<Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
							{t("pbj_model.prediction.manual_hours.preview_weekend", {
								current: currentWeekendHppd ? currentWeekendHppd.value : "—",
								delta: weekendDelta.toFixed(3),
							})}
						</Typography>
					</Box>

					{/* The jobs the prediction has no hours for, spelled out with the dates they're
					    missing — this dialog is where the user acts on the tab's warning, so it lists
					    the specifics rather than repeating the one-line summary. Not dismissible here:
					    it IS the reason the user opened the dialog. */}
					{!auditHoursSource && (
						<PbjPredictionJobCoverageStatus
							isLoading={coverageQuery.isLoading}
							isError={coverageQuery.isError}
							dataTestId="pbj-prediction-manual-hours-missing-jobs-coverage"
						/>
					)}
					{!auditHoursSource && coverageQuery.data !== undefined && !coverageQuery.isLoading && !coverageQuery.isError && (
						<MissingJobsWarning gaps={coverageQuery.data} variant="detailed" dataTestId="pbj-prediction-manual-hours-missing-jobs" />
					)}

					{/* Inline add/edit form. Lives in the dialog body so the user can iterate
					    without opening a second modal — the table updates beneath as they save. */}
					<Box
						sx={{
							p: 2.5,
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 2,
							bgcolor: "background.paper",
						}}
					>
						<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
							{form.editingId !== null ? t("pbj_model.prediction.manual_hours.edit_form_title") : t("pbj_model.prediction.manual_hours.add_form_title")}
						</Typography>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1.5fr) minmax(0, 1fr)" },
								gap: 2,
							}}
						>
							<Box data-testid="pbj-prediction-manual-hours-job">
								<JobSelector
									jobs={jobs}
									variant="dropdown"
									singleSelect
									hideAllOption
									fullWidth
									label={t("pbj_model.prediction.manual_hours.job_label")}
									selectedJobIds={form.jobId === null ? [] : [form.jobId]}
									onSelectedJobsChanged={(selected) => setForm((prev) => ({ ...prev, jobId: selected[0] ?? null }))}
								/>
							</Box>
							<SystemValidationTextField
								fullWidth
								label={t("pbj_model.prediction.manual_hours.employee_name_label")}
								value={form.employeeName}
								onChange={(e) => setForm((prev) => ({ ...prev, employeeName: e.target.value }))}
								reserveHeightForHelperText={false}
								inputProps={{ inputProps: { maxLength: EMPLOYEE_NAME_MAX_LENGTH, "data-testid": "pbj-prediction-manual-hours-employee-name" } }}
							/>
						</Box>

						{/* How the hours below are read. A segmented switch rather than a checkbox: the
						    two modes are peers, and the labels have to say which unit the fields are in. */}
						<Stack spacing={0.75} sx={{ mt: 2 }}>
							<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
								{t("pbj_model.prediction.manual_hours.mode_label")}
							</Typography>
							<SystemFlatSwitch<PbjPredictionHoursMode>
								options={[
									{
										value: "recurring",
										label: t("pbj_model.prediction.manual_hours.mode_recurring"),
										"data-testid": "pbj-prediction-manual-hours-mode-recurring",
									},
									{ value: "total", label: t("pbj_model.prediction.manual_hours.mode_total"), "data-testid": "pbj-prediction-manual-hours-mode-total" },
								]}
								activeIndexOrValue={form.hoursMode}
								onSwitch={(option) => handleModeChange(option.value)}
								delayCallback={0}
								equalWidthOptions
								textWrap="nowrap"
								rootMaxWidth={360}
								data-testid="pbj-prediction-manual-hours-mode"
							/>
						</Stack>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
								gap: 2,
								mt: 2,
							}}
						>
							<Stack spacing={0.75}>
								<SystemValidationTextField
									inputType="number"
									fullWidth
									label={
										isRecurring
											? t("pbj_model.prediction.manual_hours.weekday_hours_per_day_label")
											: t("pbj_model.prediction.manual_hours.weekday_hours_label")
									}
									value={form.weekdayHours}
									onChange={(e) => setForm((prev) => ({ ...prev, weekdayHours: e.target.value }))}
									validation={{
										validationCallback: (raw) => isHoursFieldValid(raw, hoursFieldMax),
										helperText: {
											invalid: isRecurring
												? t("pbj_model.prediction.manual_hours.invalid_per_day_hours")
												: t("pbj_model.prediction.manual_hours.invalid_hours"),
										},
										performValidation: "onlyAfterInitialChange",
										showValidWhenValid: "never",
									}}
									reserveHeightForHelperText={false}
									inputProps={{
										inputProps: { min: HOURS_MIN, max: hoursFieldMax, step: HOURS_STEP, "data-testid": "pbj-prediction-manual-hours-weekday-hours" },
									}}
								/>
								<InlineHint tone="neutral" hideIcon dataTestId="pbj-prediction-manual-hours-weekday-hint">
									{hoursHint(
										weekdayValue,
										weekdayTotal,
										formDayCounts.weekday,
										t("pbj_model.prediction.manual_hours.weekday_count", { days: formDayCounts.weekday }),
									)}
								</InlineHint>
							</Stack>
							<Stack spacing={0.75}>
								<SystemValidationTextField
									inputType="number"
									fullWidth
									label={
										isRecurring
											? t("pbj_model.prediction.manual_hours.weekend_hours_per_day_label")
											: t("pbj_model.prediction.manual_hours.weekend_hours_label")
									}
									value={form.weekendHours}
									onChange={(e) => setForm((prev) => ({ ...prev, weekendHours: e.target.value }))}
									validation={{
										validationCallback: (raw) => isHoursFieldValid(raw, hoursFieldMax),
										helperText: {
											invalid: isRecurring
												? t("pbj_model.prediction.manual_hours.invalid_per_day_hours")
												: t("pbj_model.prediction.manual_hours.invalid_hours"),
										},
										performValidation: "onlyAfterInitialChange",
										showValidWhenValid: "never",
									}}
									reserveHeightForHelperText={false}
									inputProps={{
										inputProps: { min: HOURS_MIN, max: hoursFieldMax, step: HOURS_STEP, "data-testid": "pbj-prediction-manual-hours-weekend-hours" },
									}}
								/>
								<InlineHint tone="neutral" hideIcon dataTestId="pbj-prediction-manual-hours-weekend-hint">
									{hoursHint(
										weekendValue,
										weekendTotal,
										formDayCounts.weekend,
										t("pbj_model.prediction.manual_hours.weekend_count", { days: formDayCounts.weekend }),
									)}
								</InlineHint>
							</Stack>
							<LocalizationProvider dateAdapter={AdapterLuxon}>
								<Box data-testid="pbj-prediction-manual-hours-start">
									<DatePicker
										label={t("pbj_model.prediction.manual_hours.period_start_label")}
										value={form.periodStart}
										onChange={(value) => setForm((prev) => ({ ...prev, periodStart: value }))}
										format="MM/dd/yyyy"
										slotProps={{ textField: { size: "small", fullWidth: true } }}
									/>
								</Box>
								<Box data-testid="pbj-prediction-manual-hours-end">
									<DatePicker
										label={t("pbj_model.prediction.manual_hours.period_end_label")}
										value={form.periodEnd}
										onChange={(value) => setForm((prev) => ({ ...prev, periodEnd: value }))}
										minDate={form.periodStart ?? undefined}
										format="MM/dd/yyyy"
										slotProps={{ textField: { size: "small", fullWidth: true } }}
									/>
								</Box>
							</LocalizationProvider>
							<FormControlLabel
								control={
									<Checkbox
										checked={form.isRn}
										onChange={(_e, checked) => setForm((prev) => ({ ...prev, isRn: checked }))}
										data-testid="pbj-prediction-manual-hours-isrn"
									/>
								}
								label={
									<Tooltip title={t("pbj_model.prediction.manual_hours.is_rn_tooltip")} placement="top" arrow>
										<span>{t("pbj_model.prediction.manual_hours.is_rn_label")}</span>
									</Tooltip>
								}
								sx={{ mr: 0, alignSelf: "center" }}
							/>
						</Box>

						{dateRangeInvalid && (
							<Typography variant="caption" color="error" sx={{ mt: 1.5, display: "block" }}>
								{t("pbj_model.prediction.manual_hours.invalid_date_range")}
							</Typography>
						)}

						<Stack
							direction="row"
							spacing={1}
							sx={{ justifyContent: "flex-end", alignItems: "center", mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
						>
							{/* What the entry resolves to, restated once next to the button — the summary
							    the ticket asks for, sitting where the decision to save is made. */}
							<Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }} data-testid="pbj-prediction-manual-hours-resolved-total">
								{form.jobId === null
									? t("pbj_model.prediction.manual_hours.job_required")
									: t("pbj_model.prediction.manual_hours.resolved_total", {
											hours: formatHoursValue(weekdayTotal + weekendTotal),
											days: formDayCounts.weekday + formDayCounts.weekend,
										})}
							</Typography>
							{form.editingId !== null && (
								<SystemButton
									buttonVariant="secondary"
									onClick={handleResetForm}
									size="small"
									noFullWidthMobile
									data-testid="pbj-prediction-manual-hours-cancel-edit"
								>
									{t("pbj_model.prediction.manual_hours.cancel_edit")}
								</SystemButton>
							)}
							<SystemButton
								buttonVariant="primary"
								leadingIcon={<AddIcon sx={{ fontSize: 18 }} />}
								onClick={handleSubmit}
								disabled={!canSubmit}
								noFullWidthMobile
								data-testid="pbj-prediction-manual-hours-submit"
							>
								{form.editingId !== null ? t("pbj_model.prediction.manual_hours.save") : t("pbj_model.prediction.manual_hours.add")}
							</SystemButton>
						</Stack>
					</Box>

					<Box sx={{ height: 360, width: "100%" }}>
						<SystemDataGrid<PbjPredictionManualHoursEntry>
							rows={entries}
							columns={columns}
							getRowId={(r) => r.id}
							dataTestId="pbj-prediction-manual-hours-table"
							loading={listQuery.isLoading}
							emptyState={<EmptyState title={t("pbj_model.prediction.manual_hours.empty")} dataTestId="pbj-prediction-manual-hours-empty" />}
						/>
					</Box>
				</Stack>
			}
		/>
	);
};
